import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CreditCard, Loader2 } from "lucide-react";
import { API_URL } from "../data/site";
import { money, when, StatusPill } from "./AdminQuotes";

const OPTION_LABEL = { total: "Full total", deposit: "Deposit", custom: "Custom amount" };

export const AdminPayments = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API_URL}/payments`, { withCredentials: true })
      .then((res) => setPayments(res.data))
      .catch(() => toast.error("Could not load payments."))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Loader2 className="h-5 w-5 animate-spin text-[#e63946]" />;

  if (payments.length === 0) {
    return (
      <div data-testid="payments-empty" className="flex flex-col items-center gap-4 border border-dashed border-neutral-300 py-20 text-neutral-400">
        <CreditCard className="h-8 w-8" />
        <p className="text-sm">No payments yet.</p>
      </div>
    );
  }

  return (
    <div data-testid="payments-list" className="overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead>
          <tr className="font-overline text-[10px] uppercase tracking-[0.25em] text-neutral-500">
            <th className="border-b border-neutral-200 py-3 pr-4 font-normal">Date</th>
            <th className="border-b border-neutral-200 py-3 pr-4 font-normal">Reference</th>
            <th className="border-b border-neutral-200 py-3 pr-4 font-normal">Client</th>
            <th className="border-b border-neutral-200 py-3 pr-4 font-normal">Type</th>
            <th className="border-b border-neutral-200 py-3 pr-4 text-right font-normal">Amount</th>
            <th className="border-b border-neutral-200 py-3 font-normal">Status</th>
          </tr>
        </thead>
        <tbody>
          {payments.map((p, i) => (
            <tr key={p.session_id} data-testid={`payment-item-${i}`} className="border-b border-neutral-100 align-top">
              <td className="whitespace-nowrap py-4 pr-4 text-xs uppercase tracking-[0.1em] text-neutral-500">{when(p.created_at)}</td>
              <td className="py-4 pr-4 font-overline text-[11px] tracking-[0.2em] text-[#e63946]">{p.reference || "—"}</td>
              <td className="py-4 pr-4">
                {p.client_name ? (
                  <>
                    <span className="font-medium text-[#111111]">{p.client_name}</span>
                    <br />
                    <span className="text-xs text-neutral-500">{p.client_email}</span>
                  </>
                ) : (
                  <span className="text-neutral-400">—</span>
                )}
              </td>
              <td className="py-4 pr-4 text-neutral-600">{OPTION_LABEL[p.option] || p.option}</td>
              <td data-testid={`payment-amount-${i}`} className="whitespace-nowrap py-4 pr-4 text-right font-semibold text-[#111111]">
                {money(p.amount)} <span className="text-xs font-normal uppercase text-neutral-400">{p.currency}</span>
              </td>
              <td className="py-4"><StatusPill value={p.payment_status} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
